import Axios from 'axios'
import * as React from 'react'
import OverlayLoader from '../../core/components/Loading/OverlayLoading'
import {AttachmentInfo} from '../../core/interfaces/models'
import {config} from '../../core/reducers/config'
import VectorLayer from 'ol/layer/Vector.js'
import * as ol from 'ol/index.js'
import Map from 'ol/Map.js'
import Projection from 'ol/proj/Projection.js'
import Static from 'ol/source/ImageStatic.js'
import View from 'ol/View.js'
import {getCenter} from 'ol/extent.js'
import VectorSource from 'ol/source/Vector.js'
import {getMapService} from '../../core/reducers/map'
import {DrawType} from '../../core/enums/DrawType'
import {DrawSource} from '../../core/enums/DrawSource'
import {Point} from 'ol/geom.js'
import {Style, Circle as CircleStyle, Fill, Stroke} from 'ol/style.js'
import {ImageLayer, ImageWMS, Modify} from '../../core/models'
import {IMapDispatcher} from '../../core/interfaces/dispatchers'
import {CoordinateSystem} from '../../core/enums/CoordinateSystem'
import {CsSelector} from '../../core/components/Controls/CsSelector'
import {v4 as uuidv4} from 'uuid'

// --- TPS leave-one-out error helpers ---

function tpsKernel(r2: number): number {
  return r2 < 1e-12 ? 0 : r2 * Math.log(r2)
}

function solveLinear(A: number[][], b: number[]): number[] {
  const n = b.length
  const M = A.map((row, i) => [...row, b[i]])
  for (let col = 0; col < n; col++) {
    let maxRow = col
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) maxRow = row
    }
    ;[M[col], M[maxRow]] = [M[maxRow], M[col]]
    if (Math.abs(M[col][col]) < 1e-12) continue
    for (let row = 0; row < n; row++) {
      if (row === col) continue
      const f = M[row][col] / M[col][col]
      for (let j = col; j <= n; j++) M[row][j] -= f * M[col][j]
    }
  }
  return M.map((row, i) => row[n] / row[i])
}

function fitTPS(src: [number, number][], dst: [number, number][]) {
  const n = src.length
  const sz = n + 3
  const K: number[][] = Array.from({length: sz}, () => new Array(sz).fill(0))
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i !== j) {
        const dx = src[i][0] - src[j][0]
        const dy = src[i][1] - src[j][1]
        K[i][j] = tpsKernel(dx * dx + dy * dy)
      }
    }
    K[i][n] = 1
    K[i][n + 1] = src[i][0]
    K[i][n + 2] = src[i][1]
    K[n][i] = 1
    K[n + 1][i] = src[i][0]
    K[n + 2][i] = src[i][1]
  }
  const cx = solveLinear(K, [...dst.map(p => p[0]), 0, 0, 0])
  const cy = solveLinear(K, [...dst.map(p => p[1]), 0, 0, 0])
  return {wx: cx.slice(0, n), ax: cx.slice(n), wy: cy.slice(0, n), ay: cy.slice(n)}
}

function evalTPS(
  src: [number, number][],
  wx: number[],
  ax: number[],
  wy: number[],
  ay: number[],
  p: [number, number]
): [number, number] {
  let x = ax[0] + ax[1] * p[0] + ax[2] * p[1]
  let y = ay[0] + ay[1] * p[0] + ay[2] * p[1]
  for (let i = 0; i < src.length; i++) {
    const dx = p[0] - src[i][0]
    const dy = p[1] - src[i][1]
    const k = tpsKernel(dx * dx + dy * dy)
    x += wx[i] * k
    y += wy[i] * k
  }
  return [x, y]
}

function computeTpsErrors(points: MappedPoint[]): number[] {
  const n = points.length
  if (n < 4) return []
  return points.map((_, k) => {
    const srcLoo = points.filter((__, i) => i !== k).map(p => p.imgBackendCoords as [number, number])
    const dstLoo = points.filter((__, i) => i !== k).map(p => p.mapCoords as [number, number])
    const {wx, ax, wy, ay} = fitTPS(srcLoo, dstLoo)
    const pred = evalTPS(srcLoo, wx, ax, wy, ay, points[k].imgBackendCoords as [number, number])
    const dx = pred[0] - points[k].mapCoords[0]
    const dy = pred[1] - points[k].mapCoords[1]
    return Math.sqrt(dx * dx + dy * dy)
  })
}

// ----------------------------------------

interface MappedPoint {
  id: string
  imgDisplayCoords: [number, number]
  imgBackendCoords: [number, number]
  mapCoords: [number, number]
  imageFeature: any
  mapFeature: any
}

interface Props {
  mapDispatcher: IMapDispatcher
}

interface State {
  loading: boolean
  file?: AttachmentInfo
  TiffName: string
  points: MappedPoint[]
  displayCS: CoordinateSystem
  displayTransformed: Array<[number, number]>
  editValues: {[id: string]: {x: string; y: string}}
  afterSuccessRef: boolean
  afterSuccessUpload: boolean
  error: string
  status: string
  imageHeight?: number
  imageWidth?: number
  path?: string
  extend: string
  resultLayer?: ImageLayer
  visible: boolean

  // upload Pup properties
  Description: string
  PupType: number
  EKATTE: string
  Year: number
  Quarter?: string
}

export class Georeferencer extends React.Component<Props, State> {
  private vectorSource?: VectorSource
  private _miniMap?: Map
  private mainMapModify?: any
  private highlightStyle: any

  private addResultLayer = () => {
    const mapService = getMapService()
    const id = uuidv4()
    const namespaceUrl: string = config.getConfig().urls.xyApiUrl + '/' + this.state.path + '/tiffWms'
    const params = {
      url: namespaceUrl,
      params: {
        LAYERS: 'tiff_layer',
        TILED: true
      },
      serverType: 'geoserver',
      crossOrigin: 'anonymous'
    } as any

    const resultLayer = new ImageLayer({
      source: new ImageWMS(params),
      visible: true
    })

    resultLayer.moreInfo = {
      urls: [namespaceUrl],
      guid: id,
      type: 'regular',
      refreshOnExtentChange: false,
      isActive: true
    }

    mapService.addLayer(resultLayer)
    this.setState({...this.state, resultLayer, visible: true})
  }

  constructor(props: Props) {
    super(props)
    this.highlightStyle = new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({color: 'yellow'}),
        stroke: new Stroke({color: [255, 165, 0], width: 3})
      })
    })
    this.state = {
      afterSuccessRef: false,
      loading: false,
      TiffName: '',
      error: '',
      status: '',
      points: [],
      displayCS: CoordinateSystem.WEB_MERCATOR,
      displayTransformed: [],
      editValues: {},
      imageHeight: 0,
      imageWidth: 0,
      path: '2025-11-26/d2c220e28b4b4bf4b5e56f0446b4f774/2025-11-26_d2c220e28b4b4bf4b5e56f0446b4f774.png_georeferenced.tif',
      extend: '',
      resultLayer: undefined,
      visible: false,
      // Pup properties
      Description: '',
      PupType: 5,
      EKATTE: '',
      Year: new Date().getFullYear(),
      Quarter: '',
      afterSuccessUpload: false
    }
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (this.state.afterSuccessRef && !prevState.afterSuccessRef) {
      this.addResultLayer()
    }
  }

  componentWillUnmount() {
    if (this.mainMapModify) {
      const olMap = (getMapService() as any).map
      olMap.removeInteraction(this.mainMapModify)
      this.mainMapModify = undefined
    }
  }

  private csToNumeric(cs: CoordinateSystem): number {
    switch (cs) {
      case CoordinateSystem.GEOGRAPHIC:
        return 1
      case CoordinateSystem.WEB_MERCATOR:
        return 2
      case CoordinateSystem.BGS2005:
        return 4
      case CoordinateSystem.BG2005CAD:
        return 5
      case CoordinateSystem.BG_703:
        return 6
      case CoordinateSystem.BG_705:
        return 7
      case CoordinateSystem.BG_707:
        return 8
      case CoordinateSystem.BG_709:
        return 9
      case CoordinateSystem.BG_3024:
        return 14
      case CoordinateSystem.BG_3027:
        return 15
      case CoordinateSystem.BG50324:
        return 10
      case CoordinateSystem.BG50327:
        return 11
      case CoordinateSystem.BG50621:
        return 12
      case CoordinateSystem.BG50627:
        return 13
      default:
        return 2
    }
  }

  private updateDisplayCoords(pts?: MappedPoint[], cs?: CoordinateSystem) {
    const points = pts ?? this.state.points
    const displayCS = cs ?? this.state.displayCS

    if (points.length === 0) {
      this.setState(prev => ({...prev, displayTransformed: []}))
      return
    }

    if (displayCS === CoordinateSystem.WEB_MERCATOR) {
      this.setState(prev => ({...prev, displayTransformed: points.map(p => p.mapCoords)}))
      return
    }

    Axios.post(config.getConfig().urls.xyApiUrl + '/BgTrans/TransPoints', {
      Coordinates: points.map(p => p.mapCoords),
      DestinationZone: 9,
      SourceCS: 2,
      DestinationCS: this.csToNumeric(displayCS)
    })
      .then(r => {
        this.setState(prev => ({...prev, displayTransformed: r.data}))
      })
      .catch(() => {
        // keep previous display coords on error
      })
  }

  private setupMainMapModify() {
    if (this.mainMapModify) return
    const drawing = (getMapService() as any).drawing
    const source = drawing.drawLayers[DrawSource.Edit][DrawType.Point].getSource()
    const olMap = (getMapService() as any).map

    this.mainMapModify = new Modify({source})
    this.mainMapModify.on('modifyend', (e: any) => {
      e.features.getArray().forEach((feat: any) => {
        const id = feat.get('pointId')
        if (!id) return
        const coords = feat.getGeometry().getCoordinates() as [number, number]
        this.setState(
          prev => ({
            ...prev,
            points: prev.points.map(p => (p.id === id ? {...p, mapCoords: coords, mapFeature: feat} : p))
          }),
          () => this.updateDisplayCoords()
        )
      })
    })
    olMap.addInteraction(this.mainMapModify)
  }

  private deletePoint = (id: string) => {
    const point = this.state.points.find(p => p.id === id)
    if (!point) return
    this.vectorSource?.removeFeature(point.imageFeature)
    getMapService().drawing.unMark(DrawSource.Edit, [point.mapFeature])
    this.setState(
      prev => {
        const editValues = {...prev.editValues}
        delete editValues[id]
        return {...prev, points: prev.points.filter(p => p.id !== id), editValues}
      },
      () => this.updateDisplayCoords()
    )
  }

  private highlightPoint = (id: string) => {
    const point = this.state.points.find(p => p.id === id)
    if (!point) return
    point.imageFeature.setStyle(this.highlightStyle)
    point.mapFeature.setStyle(this.highlightStyle)
  }

  private unhighlightPoint = (id: string) => {
    const point = this.state.points.find(p => p.id === id)
    if (!point) return
    point.imageFeature.setStyle(null)
    point.mapFeature.setStyle(null)
  }

  private commitCoordEdit = (pointId: string) => {
    const editVal = this.state.editValues[pointId]
    if (!editVal) return
    const x = parseFloat(editVal.x)
    const y = parseFloat(editVal.y)

    const clearEdit = () =>
      this.setState(prev => {
        const editValues = {...prev.editValues}
        delete editValues[pointId]
        return {...prev, editValues}
      })

    if (isNaN(x) || isNaN(y)) {
      clearEdit()
      return
    }

    const {displayCS, points} = this.state
    const point = points.find(p => p.id === pointId)
    if (!point) return

    const applyNewMapCoords = (newCoords: [number, number]) => {
      point.mapFeature.getGeometry().setCoordinates(newCoords)
      this.setState(
        prev => {
          const editValues = {...prev.editValues}
          delete editValues[pointId]
          return {
            ...prev,
            points: prev.points.map(p => (p.id === pointId ? {...p, mapCoords: newCoords} : p)),
            editValues
          }
        },
        () => this.updateDisplayCoords()
      )
    }

    if (displayCS === CoordinateSystem.WEB_MERCATOR) {
      applyNewMapCoords([x, y])
      return
    }

    Axios.post(config.getConfig().urls.xyApiUrl + '/BgTrans/TransPoints', {
      Coordinates: [[x, y]],
      DestinationZone: 9,
      SourceCS: this.csToNumeric(displayCS),
      DestinationCS: 2
    })
      .then(r => applyNewMapCoords([r.data[0][0], r.data[0][1]]))
      .catch(clearEdit)
  }

  getAttachmentsControl = () => {
    return (
      <div style={{display: 'flex', justifyContent: 'strech', width: '100%'}}>
        <input
          type="file"
          accept=".tiff,.jpeg,.jpg,.png"
          onChange={e => {
            if (e.target.files && e.target.files.length > 0) {
              const file = e.target.files[0]
              const attachment: AttachmentInfo = {
                name: file.name,
                type: file.type,
                file: file
              }
              this.setState({...this.state, file: attachment, error: ''})

              const reader = new FileReader()
              reader.onload = ev => {
                const img = document.createElement('img')
                img.src = ev.target!.result as string
                img.style.width = '100%'
                img.onload = () => {
                  const imageHeight = img.height
                  const imageWidth = img.width
                  this.setState({...this.state, imageHeight, imageWidth})

                  const extent = [0, 0, imageWidth, imageHeight]
                  const projection = new Projection({
                    code: 'xkcd-image',
                    units: 'pixels',
                    extent: extent
                  })
                  const vectorSource = new VectorSource()
                  this.vectorSource = vectorSource

                  const vectorLayer = new VectorLayer({
                    source: vectorSource,
                    style: () =>
                      new Style({
                        image: new CircleStyle({
                          radius: 5,
                          fill: new Fill({color: 'red'}),
                          stroke: new Stroke({color: [255, 0, 0], width: 2})
                        })
                      }) as any
                  })

                  const miniMap = new Map({
                    layers: [
                      new ImageLayer({
                        source: new Static({
                          url: img.src!,
                          projection: projection,
                          imageExtent: extent
                        })
                      }),
                      vectorLayer
                    ],
                    target: 'map',
                    view: new View({
                      projection: projection,
                      center: getCenter(extent),
                      zoom: 2,
                      maxZoom: 20
                    })
                  })
                  this._miniMap = miniMap

                  // Modify interaction for dragging existing minimap points
                  const miniModify = new Modify({source: vectorSource})
                  miniModify.on('modifyend', (ev: any) => {
                    ev.features.getArray().forEach((feat: any) => {
                      const id = feat.get('pointId')
                      if (!id) return
                      const coords = feat.getGeometry().getCoordinates() as [number, number]
                      const imgX = Math.round(coords[0] * 1000) / 1000
                      const imgY = Math.round(coords[1] * 1000) / 1000
                      const invertedY = Math.round((imageHeight - imgY) * 1000) / 1000
                      this.setState(prev => ({
                        ...prev,
                        points: prev.points.map(p =>
                          p.id === id
                            ? {
                                ...p,
                                imgDisplayCoords: [imgX, imgY],
                                imgBackendCoords: [imgX, invertedY],
                                imageFeature: feat
                              }
                            : p
                        )
                      }))
                    })
                  })
                  miniMap.addInteraction(miniModify)

                  // Setup Modify on main map draw source
                  this.setupMainMapModify()

                  // Click on minimap to add a new point pair
                  miniMap.on('click', (evt: any) => {
                    // Skip if clicking on an existing point (let Modify handle it)
                    let hitExisting = false
                    miniMap.forEachFeatureAtPixel(evt.pixel, () => {
                      hitExisting = true
                    })
                    if (hitExisting) return

                    const coordinate = evt.coordinate as [number, number]
                    const imgX = coordinate[0]
                    const imgY = coordinate[1]

                    if (imgX < 0 || imgX > imageWidth || imgY < 0 || imgY > imageHeight) return

                    const roundedX = Math.round(imgX * 1000) / 1000
                    const roundedY = Math.round(imgY * 1000) / 1000
                    const invertedY = Math.round((imageHeight - roundedY) * 1000) / 1000

                    const pointId = uuidv4()
                    const imageFeature = new ol.Feature({geometry: new Point([roundedX, roundedY])})
                    imageFeature.set('pointId', pointId)
                    vectorSource.addFeature(imageFeature)

                    // Activate draw on main map to pick the corresponding real-world coordinate
                    getMapService().drawing.activateDraw(
                      DrawType.Point,
                      DrawSource.Edit,
                      () => {},
                      f => {
                        const mapCoords = f.getGeometry().getCoordinates() as [number, number]
                        f.set('pointId', pointId)

                        const newPoint: MappedPoint = {
                          id: pointId,
                          imgDisplayCoords: [roundedX, roundedY],
                          imgBackendCoords: [roundedX, invertedY],
                          mapCoords,
                          imageFeature,
                          mapFeature: f
                        }

                        getMapService().drawing.mark([f], DrawSource.Edit)

                        this.setState(
                          prev => ({...prev, points: [...prev.points, newPoint]}),
                          () => this.updateDisplayCoords()
                        )
                      }
                    )
                  })
                }
              }
              reader.readAsDataURL(file)
            }
          }}
        ></input>
      </div>
    )
  }

  getContainer = (content: JSX.Element, title: string | JSX.Element, margin?: string): JSX.Element => {
    return (
      <div style={{margin, display: 'flex'}}>
        <div style={{minWidth: '40%', maxWidth: '40%', display: 'flex', alignItems: 'center'}}>{title}</div>
        {content}
      </div>
    )
  }

  getExecuteButton = () => {
    return (
      <button
        type="button"
        className="button width"
        disabled={!this.state.file || this.state.points.length < 4}
        style={{color: !this.state.file || this.state.points.length < 4 ? 'transparent' : undefined}}
        onClick={() => {
          const mapping = this.state.points.map(
            p => `${p.imgBackendCoords[0]},${p.imgBackendCoords[1]},${p.mapCoords[0]},${p.mapCoords[1]}`
          )
          const formData = new FormData()
          formData.append('File', this.state.file!.file!, this.state.file!.name)
          formData.append('Mapping', mapping.join('|'))
          this.setState({...this.state, loading: true}, () => {
            Axios.post(config.getConfig().urls.xyApiUrl + '/GeoreferenceTiff', formData, {
              headers: {'Content-Type': `multipart/form-data;`}
            })
              .then(r => {
                this.setState({
                  ...this.state,
                  afterSuccessRef: true,
                  error: '',
                  loading: false,
                  path: r.data.path,
                  extend: r.data.extent
                })
                this.getRefUploadForm()
              })
              .catch(error => {
                if (error.response && error.response.data && error.response.data) {
                  this.setState({...this.state, error: error.response.data})
                } else {
                  this.setState({...this.state, error: 'Възникна грешка ' + error.message, loading: false})
                }
              })
          })
        }}
      >
        Стартирай рефериране
      </button>
    )
  }

  getCoordinateRegister = (): JSX.Element => {
    const {points, displayTransformed, displayCS, editValues} = this.state
    const errors = computeTpsErrors(points)
    const meanError = errors.length > 0 ? errors.reduce((s, e) => s + e, 0) / errors.length : null
    const cell: React.CSSProperties = {border: '1px solid #ccc', padding: '2px 4px'}
    const inputStyle: React.CSSProperties = {
      width: '100%',
      border: 'none',
      background: 'transparent',
      fontSize: '11px',
      outline: 'none'
    }
    return (
      <div style={{marginTop: '8px'}}>
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '6px'}}>
          <div style={{minWidth: '40%', maxWidth: '40%'}}>Показване в:</div>
          <CsSelector
            selected={displayCS}
            onChange={cs =>
              this.setState({...this.state, displayCS: cs, editValues: {}}, () => this.updateDisplayCoords())
            }
            className="form-control"
          />
        </div>
        {points.length > 0 && (
          <table style={{width: '100%', fontSize: '11px', borderCollapse: 'collapse'}}>
            <thead>
              <tr>
                <th style={cell}>#</th>
                <th style={cell}>X</th>
                <th style={cell}>Y</th>
                {errors.length > 0 && <th style={{...cell, whiteSpace: 'nowrap'}}>Грешка (м)</th>}
                <th style={{...cell, width: '20px'}}></th>
              </tr>
            </thead>
            <tbody>
              {points.map((p, i) => {
                const transformed = displayTransformed[i]
                const edit = editValues[p.id]
                const displayX = transformed != null ? transformed[0].toFixed(4) : ''
                const displayY = transformed != null ? transformed[1].toFixed(4) : ''
                const xVal = edit ? edit.x : displayX
                const yVal = edit ? edit.y : displayY
                const err = errors[i]
                return (
                  <tr
                    key={p.id}
                    onMouseEnter={() => this.highlightPoint(p.id)}
                    onMouseLeave={() => this.unhighlightPoint(p.id)}
                  >
                    <td style={{...cell, textAlign: 'center', width: '24px'}}>{i + 1}</td>
                    <td style={cell}>
                      <input
                        style={inputStyle}
                        value={xVal}
                        onChange={e =>
                          this.setState(prev => ({
                            ...prev,
                            editValues: {
                              ...prev.editValues,
                              [p.id]: {x: e.target.value, y: prev.editValues[p.id]?.y ?? displayY}
                            }
                          }))
                        }
                        onBlur={() => this.commitCoordEdit(p.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') this.commitCoordEdit(p.id)
                        }}
                      />
                    </td>
                    <td style={cell}>
                      <input
                        style={inputStyle}
                        value={yVal}
                        onChange={e =>
                          this.setState(prev => ({
                            ...prev,
                            editValues: {
                              ...prev.editValues,
                              [p.id]: {x: prev.editValues[p.id]?.x ?? displayX, y: e.target.value}
                            }
                          }))
                        }
                        onBlur={() => this.commitCoordEdit(p.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') this.commitCoordEdit(p.id)
                        }}
                      />
                    </td>
                    {errors.length > 0 && (
                      <td style={{...cell, textAlign: 'right', color: err > 2 * (meanError ?? 0) ? '#c00' : 'inherit'}}>
                        {err.toFixed(2)}
                      </td>
                    )}
                    <td style={{...cell, width: '20px', textAlign: 'center'}}>
                      <button
                        type="button"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          color: '#999',
                          fontSize: '11px'
                        }}
                        title="Изтрий точката"
                        onClick={() => this.deletePoint(p.id)}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        {meanError != null && (
          <div style={{marginTop: '4px', fontSize: '11px', textAlign: 'right'}}>
            Средна грешка: <strong>{meanError.toFixed(2)} м</strong>
          </div>
        )}
      </div>
    )
  }

  getRefUploadForm = (): JSX.Element => {
    if (!this.state.afterSuccessRef) {
      return <div></div>
    }

    if (this.state.afterSuccessUpload) {
      return this.afterSuccessImport()
    }

    return (
      <div style={{marginTop: '10px'}}>
        {/* get Description, year, ekatte, type input fields */}
        <div style={{margin: '10px 0px', display: 'flex'}}>
          <div style={{minWidth: '40%', maxWidth: '40%', display: 'flex', alignItems: 'center'}}>Описание</div>
          <input
            className="form-control"
            style={{margin: '0 10px 0 0'}}
            value={this.state.Description}
            onChange={s => {
              this.setState({...this.state, Description: s.target.value, error: ''})
            }}
          ></input>
        </div>

        <div style={{margin: '10px 0px', display: 'flex'}}>
          <div style={{minWidth: '40%', maxWidth: '40%', display: 'flex', alignItems: 'center'}}>EKATTE</div>
          <input
            className="form-control"
            style={{margin: '0 10px 0 0'}}
            value={this.state.EKATTE}
            onChange={s => {
              this.setState({...this.state, EKATTE: s.target.value, error: ''})
            }}
          ></input>
        </div>
        <div style={{margin: '10px 0px', display: 'flex'}}>
          <div style={{minWidth: '40%', maxWidth: '40%', display: 'flex', alignItems: 'center'}}>Година</div>
          <input
            className="form-control"
            style={{margin: '0 10px 0 0'}}
            value={this.state.Year}
            onChange={s => {
              this.setState({...this.state, Year: Number(s.target.value), error: ''})
            }}
          ></input>
        </div>
        <div style={{margin: '10px 0px', display: 'flex'}}>
          <div style={{minWidth: '40%', maxWidth: '40%', display: 'flex', alignItems: 'center'}}>Квартал</div>
          <input
            className="form-control"
            style={{margin: '0 10px 0 0'}}
            value={this.state.Quarter}
            onChange={s => {
              this.setState({...this.state, Quarter: s.target.value, error: ''})
            }}
          ></input>
        </div>
        <div style={{margin: '10px 0px', display: 'flex'}}>
          <div style={{minWidth: '40%', maxWidth: '40%', display: 'flex', alignItems: 'center'}}>Тип</div>
          <select
            className="form-control"
            value={this.state.PupType}
            onChange={s => {
              this.setState({...this.state, PupType: Number(s.target.value), error: ''})
            }}
          >
            <option value={5}>ПУП</option>
            <option value={0}>ПУП - ПРЗ</option>
            <option value={1}>ПУП - ПР</option>
            <option value={2}>ПУП - ПУР</option>
            <option value={3}>ПУП - ПЗ</option>
            <option value={4}>ПУП - РУП</option>
          </select>
        </div>
        <button
          type="button"
          className="button width"
          disabled={
            !this.state.Description ||
            !this.state.EKATTE ||
            !this.state.Year ||
            this.state.PupType === undefined ||
            this.state.loading
          }
          onClick={() => {
            this.setState({...this.state, loading: true})
            const formData = new FormData()
            formData.append('Description', this.state.Description)
            formData.append('EKATTE', this.state.EKATTE)
            formData.append('Year', this.state.Year.toString())
            formData.append('Type', this.state.PupType.toString())
            formData.append('Quarter', this.state.Quarter ? this.state.Quarter : '')
            this.setState({...this.state, loading: true}, () => {
              Axios.post(config.getConfig().urls.xyApiUrl + '/' + this.state.path + '/ImportPUP', formData, {
                headers: {'Content-Type': `multipart/form-data;`}
              })
                .then(() => {
                  this.setState({
                    ...this.state,
                    afterSuccessUpload: true,
                    error: '',
                    loading: false
                  })
                })
                .catch(error => {
                  if (error.response && error.response.data && error.response.data) {
                    this.setState({...this.state, error: error.response.data})
                  } else {
                    this.setState({...this.state, error: 'Възникна грешка ' + error.message, loading: false})
                  }
                })
            })
          }}
        >
          Качи този ПУП
        </button>
      </div>
    )
  }

  afterSuccessRef() {
    return (
      <div>
        <div className="success-div">Успешено рефериране</div>
        <div className="success-div">
          <a href={config.getConfig().urls.xyApiUrl + '/GetTransformedTiff/' + this.state.path}>Свали фаила</a>
        </div>
        <button
          type="button"
          className="button width"
          onClick={() => {
            this.setState({...this.state, afterSuccessRef: false, file: undefined})
          }}
        >
          Нов файл
        </button>
        {this.state.visible && (
          <button
            type="button"
            className="button width"
            onClick={() => {
              getMapService().removeLayer(this.state.resultLayer!)
              this.setState({...this.state, visible: false})
            }}
          >
            Премахни от картата
          </button>
        )}
        {!this.state.visible && (
          <button
            type="button"
            className="button width"
            onClick={() => {
              const layer = this.state.resultLayer
              if (layer) {
                getMapService().addLayer(layer)
                this.setState({...this.state, visible: true})
              }
            }}
          >
            Покажи на картата
          </button>
        )}
        {this.getRefUploadForm()}
      </div>
    )
  }

  afterSuccessImport() {
    return (
      <div>
        <div className="success-div">Успешено качване на ПУП</div>
        <button
          type="button"
          className="button width"
          onClick={() => {
            this.setState({...this.state, afterSuccessUpload: false, afterSuccessRef: false, file: undefined})
          }}
        >
          Нов файл
        </button>
      </div>
    )
  }

  onerror() {
    return (
      <div className="error-div">
        <button
          type="button"
          className="button width"
          onClick={() => {
            this.setState({...this.state, afterSuccessRef: false, file: undefined, error: ''})
          }}
        >
          Нов файл
        </button>
        {this.state.error}
      </div>
    )
  }

  render() {
    const {points, file} = this.state
    const isDisabled = !file || points.length < 4
    const remaining = Math.max(0, 4 - points.length)
    return (
      <div
        style={
          {
            padding: '10px',
            boxSizing: 'border-box',
            height: 'calc(100% - 70px)',
            overflow: 'auto'
          } as React.CSSProperties
        }
      >
        {this.state.afterSuccessRef ? (
          this.afterSuccessRef()
        ) : this.state.error ? (
          this.onerror()
        ) : (
          <div style={{position: 'relative'}}>
            <OverlayLoader show={this.state.loading}></OverlayLoader>
            {this.getContainer(this.getAttachmentsControl(), 'Прикачете файл', `10px 0px`)}
            <div style={{position: 'relative', display: 'inline-block', width: '100%', marginTop: '5px'}}>
              {this.getExecuteButton()}
              {isDisabled && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    fontSize: '12px',
                    color: '#666'
                  }}
                >
                  {!file ? 'Изберете файл' : `Брой отбелязани точки: ${points.length}, нужни още ${remaining}`}
                </div>
              )}
            </div>
            <div id="map" style={{width: '100%', height: '500px'}}></div>
            {this.getCoordinateRegister()}
          </div>
        )}
      </div>
    )
  }
}
